import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { HardDrive, Loader2 } from 'lucide-react';
import { STAFF_PROXY_TESTING_COMMAND_EXECUTIONS } from '@staff-app/graphql-client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@staff-app/ui';
import { safeJsonParse } from '@/lib/safe-format';
import { formatBytes } from '../lib/device-metrics';
import type { CommandExecutionRow } from '../lib/types';
import { useDrawerScopeVars } from '../../../lib/drawer-mode';

/** The row-fallback poll: a few bounded looks at the command's execution row, then give up. */
export const STORAGE_POLL_DELAYS_MS = [2_000, 4_000, 6_000];

const STORAGE_WIRE_VALUE = 'getUsedStorage';

export interface StorageNumbers {
  used: string;
  free: string;
}

/** `{ value, free_value }` (bytes) from open-api's inline reply or a stored `result` string. */
export function parseStorageNumbers(raw: unknown): StorageNumbers | null {
  let v: unknown = raw;
  for (let i = 0; i < 3 && typeof v === 'string'; i += 1) {
    try {
      v = JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== 'object') return null;
  const o = v as { data?: unknown; value?: unknown; free_value?: unknown };
  const src = (o.data && typeof o.data === 'object' ? o.data : o) as {
    value?: unknown;
    free_value?: unknown;
  };
  const used = formatBytes(Number(src.value));
  const free = formatBytes(Number(src.free_value));
  return used && free ? { used, free } : null;
}

function replyCmdId(reply: unknown): string | null {
  const id = (reply as { cmdId?: unknown } | null | undefined)?.cmdId;
  return typeof id === 'string' && id ? id : null;
}

type State =
  | { kind: 'ready'; numbers: StorageNumbers }
  | { kind: 'polling' }
  | { kind: 'raw'; result: string }
  | { kind: 'none' };

/**
 * More › Storage Details. `MDM_GET_USED_STORAGE` is needs-response: open-api waits for the device and
 * returns `{ data: { value, free_value } }` inline, which is what scio3's MdmButtonRow shows. When the
 * inline reply is empty (the device answered late), this looks at the command-execution row a few
 * times — bounded — and shows its `result`; after that it points at Remote command history.
 */
export function TestingStorageDialog({
  open,
  deviceId,
  reply,
  onClose,
}: {
  open: boolean;
  deviceId: string;
  reply: unknown;
  onClose: () => void;
}) {
  const client = useApolloClient();
  const inline = parseStorageNumbers(reply);
  const [state, setState] = useState<State>(() =>
    inline ? { kind: 'ready', numbers: inline } : { kind: 'polling' },
  );

  const scope = useDrawerScopeVars();
  useEffect(() => {
    if (!open || inline) return;
    let cancelled = false;
    const cmdId = replyCmdId(reply);
    const run = async () => {
      for (const delay of STORAGE_POLL_DELAYS_MS) {
        await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        try {
          const { data } = await client.query<{ staffProxyTestingCommandExecutions: string }>({
            query: STAFF_PROXY_TESTING_COMMAND_EXECUTIONS,
            variables: { deviceId, first: 10, ...scope },
            fetchPolicy: 'network-only',
          });
          if (cancelled) return;
          const rows = safeJsonParse<CommandExecutionRow[]>(
            data?.staffProxyTestingCommandExecutions,
            [],
          );
          const row = cmdId
            ? rows.find((r) => r._id === cmdId)
            : rows.find((r) => r.command === STORAGE_WIRE_VALUE);
          if (row?.result) {
            const numbers = parseStorageNumbers(row.result);
            setState(numbers ? { kind: 'ready', numbers } : { kind: 'raw', result: row.result });
            return;
          }
        } catch {
          /* keep polling — the loop is bounded */
        }
      }
      if (!cancelled) setState({ kind: 'none' });
    };
    void run();
    return () => {
      cancelled = true;
    };
    // `reply` identity is fixed for one dialog opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deviceId, scope]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="sm:max-w-sm"
        aria-describedby={undefined}
        data-testid="testing-storage-dialog"
      >
        <DialogHeader variant="muted">
          <DialogTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-muted-foreground" />
            {'Storage Details'}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2">
          {state.kind === 'ready' ? (
            <div
              className="space-y-0.5 rounded-md border p-4"
              data-testid="testing-storage-numbers"
            >
              <div className="flex justify-between gap-4 py-1.5 text-sm">
                <span className="text-muted-foreground">{'Used storage'}</span>
                <span className="font-medium">{state.numbers.used}</span>
              </div>
              <div className="flex justify-between gap-4 py-1.5 text-sm">
                <span className="text-muted-foreground">{'Free storage'}</span>
                <span className="font-medium">{state.numbers.free}</span>
              </div>
            </div>
          ) : state.kind === 'polling' ? (
            <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              {'Waiting for the device to report…'}
            </div>
          ) : state.kind === 'raw' ? (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 font-mono text-xs">
              {state.result}
            </pre>
          ) : (
            <p
              className="py-6 text-center text-sm text-muted-foreground"
              data-testid="testing-storage-none"
            >
              {
                'Storage request sent. The device has not reported yet — check Remote command history for the result.'
              }
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
